import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { brand } from "@/i18n/translations";
import { Socials } from "@/components/site/Socials";

const LangToggle = ({ compact = false }) => {
  const { lang, setLang, langs } = useLang();
  return (
    <div
      data-testid="lang-toggle"
      className={`flex items-center gap-1 font-mono text-[11px] tracking-widest ${
        compact ? "text-ink" : ""
      }`}
    >
      {langs.map((code, i) => (
        <span key={code} className="flex items-center gap-1">
          {i > 0 && <span className="opacity-30">/</span>}
          <button
            data-testid={`lang-${code}`}
            onClick={() => setLang(code)}
            aria-pressed={lang === code}
            className={`transition-opacity duration-300 ${
              lang === code ? "opacity-100 font-medium" : "opacity-40 hover:opacity-70"
            }`}
          >
            {code.toUpperCase()}
          </button>
        </span>
      ))}
    </div>
  );
};

export const Navbar = () => {
  const { t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const dark = scrolled; // when scrolled -> light bar with dark text

  return (
    <>
      <motion.header
        data-testid="site-navbar"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          dark
            ? "bg-bone/85 backdrop-blur-xl border-b border-hairline"
            : "bg-transparent border-b border-transparent"
        }`}
      >
        <nav className="mx-auto flex max-w-[1600px] items-center justify-between px-5 py-4 md:px-10 md:py-5">
          {/* Logo + mobile socials */}
          <div
            className={`flex flex-col gap-2 transition-colors duration-500 ${
              dark ? "text-ink" : "text-bone"
            }`}
          >
            <a
              href="#top"
              data-testid="logo"
              className="flex items-center gap-3"
            >
              <img
                src={brand.logo}
                alt="Pro Beniesto — Hunting Agency Romania"
                className="h-11 w-auto md:h-14"
              />
              <span className="flex flex-col leading-none">
                <span className="font-display text-lg font-semibold uppercase tracking-[0.14em] md:text-xl">
                  {brand.name}
                </span>
                <span className="mt-1 font-mono text-[8px] uppercase tracking-[0.24em] opacity-70 md:text-[9px]">
                  {brand.tag}
                </span>
              </span>
            </a>
            <Socials
              testId="mobile-header-socials"
              size="h-[17px] w-[17px]"
              className="gap-4 pl-[2px] md:hidden"
              itemClassName="opacity-80"
            />
          </div>

          {/* Center links (desktop) */}
          <div
            className={`hidden items-center gap-9 lg:flex ${
              dark ? "text-ink" : "text-bone"
            }`}
          >
            {t.nav.links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.href.replace("#", "")}`}
                className="group relative font-body text-[13px] font-medium uppercase tracking-[0.16em]"
              >
                {l.label}
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-current transition-all duration-500 group-hover:w-full" />
              </a>
            ))}
          </div>

          {/* Right cluster */}
          <div
            className={`flex items-center gap-5 transition-colors duration-500 ${
              dark ? "text-ink" : "text-bone"
            }`}
          >
            <a
              href="#contact"
              className="hidden font-mono text-[11px] tracking-widest md:inline"
              data-testid="nav-phone"
            >
              {t.nav.phone}
            </a>
            <Socials
              testId="header-socials"
              size="h-[18px] w-[18px]"
              className="hidden gap-3.5 md:flex"
              itemClassName="opacity-80"
            />
            <span className="hidden h-4 w-px bg-current opacity-25 md:block" />
            <LangToggle compact={dark} />
            <button
              data-testid="menu-open"
              onClick={() => setOpen(true)}
              className="flex items-center gap-2 lg:hidden"
              aria-label="Open menu"
            >
              <Menu className="h-6 w-6" strokeWidth={1.4} />
            </button>
          </div>
        </nav>
      </motion.header>

      {/* Fullscreen mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] bg-olive text-bone"
          >
            <div className="flex items-center justify-between px-5 py-4">
              <span className="flex items-center gap-3">
                <img src={brand.logo} alt="Pro Beniesto" className="h-11 w-auto" />
                <span className="font-display text-lg font-semibold uppercase tracking-[0.14em]">
                  {brand.name}
                </span>
              </span>
              <button
                data-testid="menu-close"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-7 w-7" strokeWidth={1.4} />
              </button>
            </div>
            <div className="flex h-[calc(100%-64px)] flex-col justify-center gap-3 px-6">
              {t.nav.links.map((l, i) => (
                <motion.a
                  key={l.href}
                  href={l.href}
                  data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.6 }}
                  className="font-display text-5xl font-medium uppercase leading-tight"
                >
                  {l.label}
                </motion.a>
              ))}
              <div className="mt-10 flex items-center justify-between border-t border-bone/20 pt-6">
                <span className="font-mono text-xs tracking-widest">
                  {t.nav.phone}
                </span>
                <LangToggle />
              </div>
              <Socials testId="mobile-socials" className="mt-6 text-bone/90" />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
